import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Bot, Loader2, RotateCcw, Send, Sparkles } from "lucide-react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "./ui/sheet";
import { useAuth } from "../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const KEY = "crm_assistant_state_v1";

const SUGGESTIONS = [
  "Ce înseamnă statusul business „Validat”?",
  "Cum se stabilește luna de comisionare?",
  "Unde văd rândurile din coada de identitate?",
];

function loadState(userId) {
  try {
    const s = JSON.parse(sessionStorage.getItem(KEY) || "null");
    if (s && s.userId === userId) return s;
  } catch (_) {}
  return { userId, sessionId: crypto.randomUUID(), messages: [] };
}

export function AssistantPanel({ open, onOpenChange }) {
  const { user, token } = useAuth();
  const location = useLocation();
  const [state, setState] = useState(() => loadState(user?.id));
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { sessionStorage.setItem(KEY, JSON.stringify(state)); }, [state]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [state.messages, open]);

  const send = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setState((s) => ({ ...s, messages: [...s.messages, { role: "user", text }, { role: "assistant", text: "" }] }));
    const append = (fn) => setState((s) => {
      const msgs = s.messages.slice();
      msgs[msgs.length - 1] = fn(msgs[msgs.length - 1]);
      return { ...s, messages: msgs };
    });
    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: state.sessionId, message: text, page: location.pathname }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `Eroare ${res.status}`);
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.delta) append((m) => ({ ...m, text: m.text + ev.delta }));
          if (ev.error) append((m) => ({ ...m, text: m.text || "Asistentul nu a putut răspunde.", error: true }));
        }
      }
    } catch (e) {
      append((m) => ({ ...m, text: e.message || "Eroare de rețea", error: true }));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    try { await axios.post(`${API}/assistant/reset`, { session_id: state.sessionId }); } catch (_) {}
    setState({ userId: user?.id, sessionId: crypto.randomUUID(), messages: [] });
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-md" data-testid="assistant-panel">
        <SheetHeader className="border-b border-slate-200 bg-[#0f172a] px-5 py-4 text-left">
          <SheetTitle className="flex items-center gap-2 text-slate-50">
            <Sparkles className="h-4 w-4 text-sky-400" /> Asistent CRM
          </SheetTitle>
          <SheetDescription className="text-slate-400">
            GPT-5.4 Mini · doar explicații, nu citește și nu modifică date. Conversația se păstrează doar în acest tab.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-3 overflow-y-auto bg-slate-50 px-4 py-4" data-testid="assistant-messages">
          {state.messages.length === 0 && (
            <div className="space-y-2" data-testid="assistant-empty">
              <p className="text-sm text-slate-500">Întreabă-mă despre statusuri, reguli de comisionare, importuri sau unde găsești ceva în CRM.</p>
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => send(s)}
                  data-testid={`assistant-suggestion-${i}`}
                  className="block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-700 transition-colors hover:border-sky-400 hover:bg-sky-50"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          {state.messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                data-testid={`assistant-message-${m.role}`}
                className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "whitespace-pre-wrap rounded-br-sm bg-[#1e3a8a] text-white"
                    : m.error
                      ? "rounded-bl-sm border border-rose-200 bg-rose-50 text-rose-700"
                      : "rounded-bl-sm border border-slate-200 bg-white text-slate-800 [&_p]:my-1 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_strong]:font-semibold [&_code]:rounded [&_code]:bg-slate-100 [&_code]:px-1"
                }`}
              >
                {m.text
                  ? m.role === "assistant" ? <ReactMarkdown>{m.text}</ReactMarkdown> : m.text
                  : busy && i === state.messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : ""}
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <div className="border-t border-slate-200 bg-white p-3">
          <div className="flex items-end gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              rows={2}
              placeholder="Scrie o întrebare… (Enter pentru a trimite)"
              className="min-h-[44px] resize-none"
              data-testid="assistant-input"
              disabled={busy}
            />
            <Button onClick={() => send()} disabled={busy || !input.trim()} size="icon" className="bg-[#1e3a8a] hover:bg-[#1e40af]" data-testid="assistant-send">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-slate-400">
            <span className="flex items-center gap-1"><Bot className="h-3 w-3" /> Nu ia decizii și nu scrie în CRM.</span>
            <button type="button" onClick={reset} disabled={busy} className="flex items-center gap-1 hover:text-slate-700" data-testid="assistant-reset">
              <RotateCcw className="h-3 w-3" /> Conversație nouă
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
